// Input composer: devices + action resolver behind the one `Input` object
// FrameCtx carries. Systems query typed actions (src/game/controls.ts defines
// the table); the raw mouse survives as a world-space aim point.

import type { Camera } from '../camera'
import { createActions, type ActionDefs, type Actions } from './actions'
import { createGamepadDevice, type GamepadTuning } from './gamepad'
import { createKeyboardMouseDevice } from './keyboard-mouse'

export { createActions } from './actions'
export type { ActionDefs, Actions, Binding, ButtonDef, DeviceKind, DeviceSnapshot, InputContext, VectorDef } from './actions'
export { compositeKeyVector, scaledRadialDeadzone, type Vec2 } from './deadzone'
export type { GamepadTuning } from './gamepad'

const DEFAULT_PAD_TUNING: GamepadTuning = { innerDeadzone: 0.15, outerDeadzone: 0.95 }

export interface Input<B extends string = string, V extends string = string> {
  /** World-space cursor + primary-button state. Aim/aiming only — verbs go through actions. */
  mouse: { x: number; y: number; down: boolean }
  focused: boolean
  actions: Actions<B, V>
  /** ms timestamp of the last mouse-only signal (motion/button/wheel) — drives
   *  aim source selection (systems/aim.ts); never bumped by keys. */
  readonly mouseActivityAt: number
  /** Connection status of the (single, first-found) active gamepad. */
  readonly pad: { connected: boolean; recognized: boolean; justDisconnected: boolean }
  /** Haptics garnish (stretch) — no-ops if no pad is active or the browser
   *  doesn't support vibrationActuator. */
  rumble(durationMs: number, weak: number, strong: number): void
  tick(nowMs: number): void
}

export function createInput<B extends string, V extends string>(
  canvas: HTMLCanvasElement,
  camera: Camera,
  defs: ActionDefs<B, V>,
  padTuning: GamepadTuning = DEFAULT_PAD_TUNING,
): Input<B, V> {
  const kbm = createKeyboardMouseDevice(canvas, camera)
  const pad = createGamepadDevice(padTuning)
  const actions = createActions(defs)
  const mouseWorld = { x: 0, y: 0, down: false }

  let focused = typeof document === 'undefined' ? true : !document.hidden
  const onLostFocus = (): void => {
    focused = false
    kbm.clearOnBlur()
  }
  const onRegainedFocus = (): void => {
    focused = true
  }
  window.addEventListener('blur', onLostFocus)
  window.addEventListener('focus', onRegainedFocus)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) onLostFocus()
    else onRegainedFocus()
  })

  return {
    mouse: mouseWorld,
    get focused() {
      return focused
    },
    actions,
    get mouseActivityAt() {
      return kbm.mouseActivityAt
    },
    pad: {
      get connected() {
        return pad.connected
      },
      get recognized() {
        return pad.recognized
      },
      get justDisconnected() {
        return pad.justDisconnected
      },
    },
    rumble(durationMs, weak, strong) {
      pad.rumble(durationMs, weak, strong)
    },
    tick(nowMs: number) {
      const w = camera.screenToWorld(kbm.mouseScreen.x, kbm.mouseScreen.y)
      mouseWorld.x = w.x
      mouseWorld.y = w.y
      mouseWorld.down = kbm.mouseScreen.down && focused
      pad.poll(focused)
      actions.update([kbm, pad], nowMs, focused)
    },
  }
}
